import React from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import { Flex, Box } from "@chakra-ui/react";
import environment from "src/utils/environment";
import { withApp } from "src/components/App/Context";
import NavigationBar from "src/components/NavigationBar";
import ScrollBox from "src/atoms/ScrollBox";
import { withTranslation } from "react-i18next";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import Dialog from "src/components/Dialog";
import CommandView from "./CommandView";
import { isFreemium } from "src/modules/auth/duck/selectors";
import { deleteCommand } from "src/modules/command/ajax/cloud";
import { withPurchase } from "src/modules/purchase/PurchaseContext";
import * as actions from "src/modules/command/duck/actions";
import commandFiles from "src/modules/command/command-files";
import decodeOgg from "src/browser/decodeOgg";

const log = window.require("electron-log");

class CommandsList extends React.Component {
  constructor() {
    super();
    this.audio = new Audio();
    const audioPlayer = {
      commandId: null,
      audio: this.audio,
      getCurrentTime: this.getCurrentTime
    };
    this.state = {
      error: false,
      isDeleting: false,
      commandToDeleteId: null,
      audioPlayer
    };
  }

  componentDidMount() {
    this.audio.addEventListener("ended", this.handleEnded);
  }
  componentWillUnmount() {
    this.audio.removeEventListener("ended", this.handleEnded);
    this.audio.pause();
  }
  getCurrentTime = () => this.audio.currentTime * 1000;

  handleEnded = (e) => {
    this.setState(({ audioPlayer }) => ({
      audioPlayer: {
        ...audioPlayer,
        commandId: null
      }
    }));
  };

  toggleCommandReplay = (commandId) => {
    const { audioPlayer } = this.state;
    const { commands } = this.props;

    if (audioPlayer.commandId === commandId) {
      if (this.audio.paused) {
        this.audio.play();
      } else {
        this.audio.pause();
        this.setState(({ audioPlayer }) => ({
          audioPlayer: {
            ...audioPlayer,
            commandId: null
          }
        }));
      }
      return;
    }
    this.audio.pause();
    if (environment.isBrowser) {
      this.audio.src = commands[commandId].audioFile.url;
    } else {
      this.audio.src = commandFiles.getSrc(commands[commandId].audioFile.name);
    }
    this.audio.onerror = () => this.tryPlayDecoded();
    this.audio.play();
    this.setState(({ audioPlayer }) => ({
      audioPlayer: {
        ...audioPlayer,
        commandId
      }
    }));
  };

  onDelete = (commandId) => {
    this.setState({ commandToDeleteId: commandId });
  };

  handleDelete = async () => {
    const { commandToDeleteId } = this.state;
    const { dispatch, app, commands } = this.props;
    const { audioFile } = commands[commandToDeleteId];
    this.setState({ isDeleting: true });
    try {
      await deleteCommand(commandToDeleteId);
      dispatch(actions.deleteCommand(commandToDeleteId));
      app.synchronizer.announceItemChange(
        app.synchronizer.SynchronizableItems.COMMANDS
      );
      audioFile && commandFiles.delete(audioFile.name);
      this.setState({ commandToDeleteId: null, isDeleting: false });
    } catch (error) {
      this.setState({
        error: true,
        commandToDeleteId: null,
        isDeleting: false
      });
      log.error(error);
    }
  };

  tryPlayDecoded = async () => {
    const response = await fetch(this.audio.src);
    const arrayBuffer = await response.arrayBuffer();
    decodeOgg(this.audio.src, arrayBuffer);
  };

  render() {
    const { audioPlayer, error, commandToDeleteId, isDeleting } = this.state;
    const {
      commands,
      openEditor,
      onBack,
      onClose,
      isFreeVersion,
      purchase,
      t
    } = this.props;
    const commandsArray = Object.values(commands);

    return (
      <>
        <GenericFailureDialog
          isOpen={error}
          onDismiss={() => this.setState({ error: false })}
        />
        <Dialog
          isOpen={Boolean(commandToDeleteId)}
          type="warning"
          heading={t("command.deleteCommandDialog.heading")}
          text={t("command.deleteCommandDialog.text")}
          isProcessing={isDeleting}
          processingText={t("command.deleting")}
          buttons={[
            {
              text: t("buttons.cancel"),
              onClick: () => this.setState({ commandToDeleteId: null })
            },
            {
              text: t("buttons.delete"),
              onClick: this.handleDelete
            }
          ]}
        />
        <NavigationBar
          headingKey="command.commands"
          actionButton={{
            onClick: () =>
              isFreeVersion
                ? purchase.open({ content: "playAndRecord" })
                : openEditor(),
            labelKey: "command.openCommandsEditor"
          }}
          onBack={onBack}
          onClose={onClose}
        />
        <Box py="9px" height="calc(100% - 61px)">
          <ScrollBox height="100%">
            <Flex
              alignItems="start"
              justifyContent="space-between"
              flexWrap="wrap"
              px="24px"
            >
              {commandsArray.map((command, id) => {
                const commandsLength = commandsArray.length;
                const isArrayOdd = commandsLength % 2 === 1;
                const isArrayEven = commandsLength % 2 === 0;
                const isLast = id === commandsLength - 1;
                const isLastButOne = id === commandsLength - 2;

                let mb = "16px";
                if (isArrayOdd && isLast) {
                  mb = "0px";
                } else if (isArrayEven && (isLast || isLastButOne)) {
                  mb = "0px";
                }

                return (
                  <Box key={command.objectId} mb={mb} width="280px">
                    <CommandView
                      audioPlayer={
                        audioPlayer.commandId === command.objectId
                          ? audioPlayer
                          : null
                      }
                      toggleCommandReplay={() => {
                        isFreeVersion
                          ? purchase.open({ content: "playAndRecord" })
                          : this.toggleCommandReplay(command.objectId);
                      }}
                      openEditor={() => openEditor(command.objectId)}
                      onDelete={() => this.onDelete(command.objectId)}
                      {...command}
                    />
                  </Box>
                );
              })}
            </Flex>
          </ScrollBox>
        </Box>
      </>
    );
  }
}
function mapStateToProps(state) {
  return {
    isFreeVersion: isFreemium(state)
  };
}

const enhance = compose(
  withApp,
  withPurchase,
  connect(mapStateToProps),
  withTranslation()
);

export default enhance(CommandsList);
